// =============================================================================
// useStoreFlow — this rooftop's stage list ("flow").
// -----------------------------------------------------------------------------
// Each store under a tenant can shape its own flow: stores/{storeId}
//   .apps.recon.stages[] = [{ id, name, slaHours, owner, exitChecklist:[{id,label,required}], enabled }]
// When the array is missing/empty the store runs the default STAGES from
// lib/stages.js. The server (advanceVehicleStage / scanForSlaBreaches) reads
// the same field, so the gate and the board always agree.
//
//   const { stages, allStages, custom, store, loading } = useStoreFlow();
//   stages    — enabled stages, in order (what the board shows)
//   allStages — every configured stage incl. disabled (what Flow edits)
//
// Also exports pure helpers that take a stage list so pages don't need to
// reach for the global STAGES:
//   stageOf(v)                     — vehicle's stage id
//   bucketFor(v, stages)           — 'ok' | 'warn' | 'over'
//   hoursIn(v)                     — hours in current stage
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { STAGES } from "./stages.js";
import { toMs } from "./useTenantData.js";

export const DEFAULT_FLOW = STAGES.map((s) => ({
  id: s.id, name: s.name, slaHours: s.slaHours, owner: "",
  exitChecklist: (s.exitChecklist || []).map((c) => ({ ...c })), enabled: true,
}));

export function normalizeStages(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const out = arr
    .filter((s) => s && typeof s.id === "string" && s.id.trim())
    .map((s) => ({
      id: String(s.id).slice(0, 40),
      name: String(s.name || s.id).slice(0, 60),
      slaHours: Number.isFinite(Number(s.slaHours)) ? Number(s.slaHours) : 24,
      owner: String(s.owner || "").slice(0, 80),
      enabled: s.enabled !== false,
      exitChecklist: Array.isArray(s.exitChecklist)
        ? s.exitChecklist.filter((c) => c && typeof c.id === "string").map((c) => ({ id: String(c.id).slice(0, 40), label: String(c.label || c.id).slice(0, 120), required: !!c.required }))
        : [],
    }));
  return out.length ? out : null;
}

export function useStoreFlow() {
  const { profile } = useAuth();
  const storeId = profile?.storeId || null;
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(!!storeId);

  useEffect(() => {
    if (!storeId) { setLoading(false); return undefined; }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, "stores", storeId),
      (snap) => { setStore(snap.exists() ? { id: snap.id, ...snap.data() } : null); setLoading(false); },
      (err) => { console.warn("[useStoreFlow] store read failed:", err?.message); setLoading(false); }
    );
    return unsub;
  }, [storeId]);

  const allStages = useMemo(() => normalizeStages(store?.apps?.recon?.stages) || DEFAULT_FLOW, [store]);
  const custom = !!normalizeStages(store?.apps?.recon?.stages);
  const stages = useMemo(() => allStages.filter((s) => s.enabled !== false), [allStages]);
  const byId = useMemo(() => Object.fromEntries(stages.map((s) => [s.id, s])), [stages]);

  return { stages, allStages, byId, custom, store, storeId, loading };
}

// ---------- pure helpers ----------
export const stageOf = (v) => v?.currentStage || v?.currentStep || "acquisition";

export function hoursIn(v) {
  const ms = toMs(v?.stageEnteredAt || v?.stepEnteredAt);
  if (!ms) return 0;
  return (Date.now() - ms) / 36e5;
}

export function bucketFor(v, stages) {
  const st = (stages || []).find((s) => s.id === stageOf(v));
  const sla = st?.slaHours || 24;
  const h = hoursIn(v);
  if (h > sla) return "over";
  if (h > sla * 0.7) return "warn";
  return "ok";
}

export const isActiveVehicle = (v) => !(v?.status && /sold|archived|deleted|wholesaled/i.test(String(v.status))) && !v?.archivedAt;

export function fmtDays(hours) {
  if (hours == null) return "—";
  if (hours < 1) return "just now";
  if (hours < 24) return `${Math.floor(hours)} h`;
  return `${(hours / 24).toFixed(hours / 24 >= 10 ? 0 : 1)} d`;
}

export function fmtSla(h) {
  if (!h) return "—";
  return h % 24 === 0 && h >= 48 ? `${h / 24} d` : `${h} h`;
}
